import { createContext, useContext, useState } from 'react';
import { useNotify } from './NotificationContext';
import { authenticateBiometric } from '../utils/webauthn';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [userLoading, setUserLoading] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    const { error } = useNotify();

    // Environment configuration
    const DEV = process.env.REACT_APP_DEV;
    const APP_URL = process.env.NODE_ENV === 'development'
        ? 'http://localhost:5050'
        : 'https://api.clockwisecpa.app';
    // const APP_URL = 'https://api.clockwisecpa.app'
    const FILE_APP_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:5151' : 'https://fileapi.clockwisecpa.app'

    // Enhanced cookie utility with debugging
    const getCookie = (name) => {
        
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        
        if (parts.length === 2) {
            const cookieValue = parts.pop().split(';').shift();
            return cookieValue;
        }
        
        return '';
    };

    // Enhanced API request helper with better error handling
    const apiRequest = async (endpoint, options = {}) => {
        const url = `${APP_URL}${endpoint}`;
        
        const defaultOptions = {
            credentials: 'include', // This is crucial for cookies
            headers: {
                'Content-Type': 'application/json',
                ...options.headers,
            },
        };

        // Add CSRF token for non-GET requests
        if (options.method && options.method !== 'GET') {
            const csrfToken = getCookie('csrf_access_token');
            if (csrfToken) {
                defaultOptions.headers['X-CSRF-TOKEN'] = csrfToken;
            }
        }

        const config = { ...defaultOptions, ...options };
        
        try {
            const response = await fetch(url, config);
            
            return response;
        } catch (err) {
            console.error('❌ API Request Error:', err);
            throw err;
        }
    };

    // Enhanced getUser function with cookie verification
    const getUser = async (options = {}) => {
        const { 
            cache = true,           // Use cached user if available
            forceRefresh = false,   // Force a fresh API call
            throwOnError = false    // Throw errors instead of returning null
        } = options;

        // Don't fetch user if we're in the middle of logging out
        if (isLoggingOut) {
            if (throwOnError) {
                throw new Error('Currently logging out');
            }
            return null;
        }

        // Return cached user if available and not forcing refresh
        if (cache && user && !forceRefresh) {
            return user;
        }

        // Check if we have a token
        const accessToken = getCookie('csrf_access_token');
        if (!accessToken) {
            console.log('❌ No access token found, user not authenticated');
            if (throwOnError) {
                throw new Error('No authentication token found');
            }
            setUser(null);
            return null;
        }

        console.log('🔍 Getting user with access token:', accessToken.substring(0, 10) + '...');
        setUserLoading(true);

        try {
            const response = await apiRequest('/user');
            
            if (response.ok) {
                const userData = await response.json();
                console.log('✅ User data retrieved:', userData.username || userData.id);
                setUser(userData);
                return userData;
            } 
            
            // If 401, try to refresh token
            if (response.status === 401) {
                console.log('🔄 Access token expired, trying refresh...');
                try {
                    const data = await response.json();
                    if (data['msg'] && data['msg'].startsWith('Missing')) {
                        console.log('❌ Token missing entirely');
                        setUser(null);
                        return null;
                    } else {
                        const refreshed = await attemptTokenRefresh();
                        if (refreshed && user) {
                            return user;
                        }
                    }
                } catch (parseError) {
                    console.log('⚠️ Could not parse 401 response');
                }
            }
            
            // Other errors
            console.log('❌ Failed to get user data, status:', response.status);
            setUser(null);
            if (throwOnError) {
                throw new Error(`Failed to get user: ${response.status}`);
            }
            return null;
            
        } catch (err) {
            console.error('❌ Error getting user:', err);
            setUser(null);
            if (throwOnError) {
                throw err;
            }
            return null;
        } finally {
            setUserLoading(false);
        }
    };

    // Permission checking function
    const hasPermission = async (permission, options = {}) => {
        if (!permission) {
            console.warn('hasPermission called without permission parameter');
            return false;
        }

        try {
            const userData = await getUser(options);
            
            if (!userData?.full_role) {
                return false;
            }

            // Check for admin access (all permissions)
            if (userData.full_role['all']) {
                return true;
            }

            // Check specific permission
            return !!userData.full_role[permission];
            
        } catch (err) {
            console.error('Permission check failed:', err);
            return false;
        }
    };

    // Require permission function - throws error if permission not found
    const requirePermission = async (permission, options = {}) => {
        const hasAccess = await hasPermission(permission, options);
        
        if (!hasAccess) {
            throw new Error(`Permission required: ${permission}`);
        }
        
        return true;
    };

    // Simplified user refresh function - now just calls getUser
    const refreshUser = async () => {
        try {
            console.log('🔄 Refreshing user data...');
            const userData = await getUser({ forceRefresh: true });
            return !!userData;
        } catch (err) {
            console.error('❌ User refresh failed:', err);
            return false;
        }
    };

    // Token refresh helper
    const attemptTokenRefresh = async () => {
        console.log('🔄 Attempting token refresh...');
        try {
            const response = await apiRequest('/refresh', {
                method: 'POST',
                headers: {
                    'X-CSRF-TOKEN': getCookie('csrf_refresh_token')
                }
            });

            if (response.ok) {
                const userData = await response.json();
                console.log('✅ Token refresh successful');
                setUser(userData);
                return true;
            }
            
            console.log('❌ Token refresh failed, status:', response.status);
            setUser(null);
            return false;
        } catch (err) {
            console.error('❌ Token refresh error:', err);
            setUser(null);
            return false;
        }
    };

    // Enhanced login function with detailed logging
    const login = async (username, password) => {
        if (!username || !password) {
            return { success: false, error: 'Username and password are required' };
        }

        console.log('🔐 Starting login process for user:', username);
        setIsLoading(true);
        
        try {
            const response = await apiRequest('/login', {
                method: 'POST',
                body: JSON.stringify({ username, password })
            });

            const data = await response.json();
            console.log('📄 Login response data:', data);

            if (response.ok) {
                // Give cookies time to be set
                await new Promise(resolve => setTimeout(resolve, 500));
                
                // Check if cookies were actually set
                const accessToken = getCookie('csrf_access_token');
                const refreshToken = getCookie('csrf_refresh_token');
                
                console.log('🔍 Post-login cookie check:');
                console.log('  - Access token:', accessToken ? 'Found ✅' : 'Missing ❌');
                console.log('  - Refresh token:', refreshToken ? 'Found ✅' : 'Missing ❌');

                // Check if 2FA is required
                if (data.requires_2fa || data.requires2FA || data.success) {
                    console.log('🔐 2FA required for login');
                    return {
                        success: true,
                        requires2FA: true,
                        message: data.message,
                        hasBiometric: data.has_biometric || data.hasBiometric || false,
                        hasEmail: data.has_email || data.hasEmail || true // Default to true since email is the fallback
                    };
                }
                
                // Direct login success (no 2FA)
                console.log('✅ Direct login successful (no 2FA required)');
                setUser(data.user || data);
                return { success: true, user: data.user || data };
            }

            console.log('❌ Login failed:', data.error);
            return { success: false, error: data.error || 'Login failed' };
            
        } catch (err) {
            console.error('❌ Login network error:', err);
            return { success: false, error: 'Network error during login' };
        } finally {
            setIsLoading(false);
        }
    };

    // Enhanced 2FA submission
    const submit2FA = async (code) => {
        if (!code || code.length !== 8) {
            return { success: false, error: 'Please enter a valid 8-digit code' };
        }

        console.log('🔐 Submitting 2FA code...');
        setIsLoading(true);

        try {
            const response = await apiRequest('/login', {
                method: 'POST',
                body: JSON.stringify({ '2fa_code': code })
            });

            const data = await response.json();
            console.log('📄 2FA response data:', data);

            if (response.ok) {
                // Give cookies time to be set
                await new Promise(resolve => setTimeout(resolve, 500));
                
                // Verify cookies were set
                const accessToken = getCookie('csrf_access_token');
                const refreshToken = getCookie('csrf_refresh_token');
                
                console.log('🔍 Post-2FA cookie check:');
                console.log('  - Access token:', accessToken ? 'Found ✅' : 'Missing ❌');
                console.log('  - Refresh token:', refreshToken ? 'Found ✅' : 'Missing ❌');
                
                if (!accessToken) {
                    console.error('❌ 2FA successful but no access token cookie found!');
                    return { success: false, error: 'Authentication succeeded but session not established' };
                }
                
                console.log('✅ 2FA verification successful');
                setUser(data.user || data);
                return { success: true, user: data.user || data };
            }

            console.log('❌ 2FA failed:', data.error);
            return { success: false, error: data.error || 'Invalid verification code' };
            
        } catch (err) {
            console.error('❌ 2FA network error:', err);
            return { success: false, error: 'Network error during verification' };
        } finally {
            setIsLoading(false);
        }
    };

    // Helper function to clear cookies properly
    const clearAuthCookies = () => {
        console.log('🧹 Clearing authentication cookies...');
        
        // Clear cookies with proper expiration for different possible paths and domains
        const cookiesToClear = ['csrf_access_token', 'csrf_refresh_token'];
        const domains = [window.location.hostname, `.${window.location.hostname}`, 'localhost', '.localhost'];
        const paths = ['/', '/login', '/dashboard'];
        
        cookiesToClear.forEach(cookieName => {
            // Clear with different path and domain combinations
            paths.forEach(path => {
                domains.forEach(domain => {
                    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain};`;
                    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path};`;
                });
            });
            // Also clear without specifying domain/path
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
        });
        
        // Verify cookies were cleared
        setTimeout(() => {
            console.log('🍪 Cookies after clearing:', document.cookie);
        }, 100);
    };

    // Enhanced logout function
    const logout = async () => {
        console.log('👋 Starting logout process...');
        setIsLoading(true);
        setIsLoggingOut(true); // Set logout state

        try {
            // Clear user state immediately
            setUser(null);
            
            // Attempt to notify server
            try {
                const response = await apiRequest('/logout', {
                    method: 'DELETE'
                });
                
                if (response.status === 204 || response.ok) {
                    console.log('✅ Server logout successful');
                } else {
                    console.log('⚠️ Server logout failed but continuing...');
                }
            } catch (logoutError) {
                console.log('⚠️ Server logout error but continuing...', logoutError);
            }

            // Clear cookies regardless of server response
            clearAuthCookies();

            console.log('✅ Logout process completed');
            return { success: true };
            
        } catch (err) {
            console.error('❌ Logout error:', err);
            // Still clear cookies even if there's an error
            clearAuthCookies();
            return { success: false, error: 'Logout completed with errors' };
        } finally {
            setIsLoading(false);
            // Clear logout state after a brief delay to prevent race conditions
            setTimeout(() => setIsLoggingOut(false), 500);
        }
    };

    // Password reset function
    const requestPasswordReset = async (email) => {
        if (!email) {
            return { success: false, error: 'Email is required' };
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            return { success: false, error: 'Invalid email address' };
        }

        console.log('📧 Requesting password reset for:', email);
        setIsLoading(true);

        try {
            const response = await apiRequest('/reset_password/send', {
                method: 'POST',
                body: JSON.stringify({ email })
            });

            if (response.ok) {
                console.log('✅ Password reset email sent');
                return { success: true, message: 'Password reset email sent' };
            }

            const data = await response.json();
            console.log('❌ Password reset failed:', data.error);
            return { success: false, error: data.error || 'Failed to send reset email' };
            
        } catch (err) {
            console.error('❌ Password reset network error:', err);
            return { success: false, error: 'Network error during password reset' };
        } finally {
            setIsLoading(false);
        }
    };

    // Biometric authentication for 2FA
    const authenticateWithBiometricFunc = async (username) => {
        if (!username) {
            return { success: false, error: 'Username is required for biometric authentication' };
        }

        console.log('🔐 Starting biometric 2FA authentication for:', username);
        setIsLoading(true);

        try {
            // Step 1: Get authentication options from backend
            const optionsResponse = await apiRequest('/api/webauthn/authenticate/options', {
                method: 'POST',
                body: JSON.stringify({ username })
            });

            if (!optionsResponse.ok) {
                const errorData = await optionsResponse.json();
                console.log('❌ Failed to get biometric options:', errorData);
                return { success: false, error: errorData.error || 'Failed to get authentication options' };
            }

            const options = await optionsResponse.json();
            console.log('📄 Received biometric authentication options');

            // Step 2: Perform WebAuthn authentication
            const credential = await authenticateBiometric(options);
            console.log('✅ WebAuthn authentication successful');

            // Step 3: Send credential to backend for verification
            const verifyResponse = await apiRequest('/api/webauthn/authenticate/verify', {
                method: 'POST',
                body: JSON.stringify({
                    username,
                    credential
                })
            });

            const data = await verifyResponse.json();
            console.log('📄 Biometric verification response:', data);

            if (verifyResponse.ok) {
                // Give cookies time to be set
                await new Promise(resolve => setTimeout(resolve, 500));

                // Verify cookies were set
                const accessToken = getCookie('csrf_access_token');
                console.log('🔍 Post-biometric cookie check:', accessToken ? 'Found ✅' : 'Missing ❌');

                if (!accessToken) {
                    console.error('❌ Biometric auth successful but no access token cookie found!');
                    return { success: false, error: 'Authentication succeeded but session not established' };
                }

                console.log('✅ Biometric 2FA verification successful');
                setUser(data.user || data);
                return { success: true, user: data.user || data };
            }

            console.log('❌ Biometric verification failed:', data.error);
            return { success: false, error: data.error || 'Biometric verification failed' };

        } catch (err) {
            console.error('❌ Biometric authentication error:', err);
            return { success: false, error: err.message || 'Biometric authentication failed' };
        } finally {
            setIsLoading(false);
        }
    };

    // Debug function for cookie troubleshooting
    const debugCookies = () => {
        console.log('=== 🍪 COOKIE DEBUGGING ===');
        console.log('Current URL:', window.location.href);
        console.log('Document domain:', document.domain);
        console.log('Document cookies:', document.cookie);
        console.log('LocalStorage available:', typeof(Storage) !== "undefined");
        
        // Test setting a simple cookie
        document.cookie = "debug_test=123; path=/";
        console.log('After setting test cookie:', document.cookie);
        
        // Check if running in secure context
        console.log('Secure context:', window.isSecureContext);
        console.log('Protocol:', window.location.protocol);
        
        // Check for browser extensions blocking cookies
        console.log('Navigator cookieEnabled:', navigator.cookieEnabled);
        
        // Check specific auth cookies
        const accessToken = getCookie('csrf_access_token');
        const refreshToken = getCookie('csrf_refresh_token');
        
        console.log('🔍 Auth Cookie Status:');
        console.log('  - Access Token:', accessToken ? 'Present ✅' : 'Missing ❌');
        console.log('  - Refresh Token:', refreshToken ? 'Present ✅' : 'Missing ❌');
        
        console.log('=== END COOKIE DEBUG ===');
    };

    // Context value
    const value = {
        // State
        user,                    // Current cached user (can be null)
        isLoading,              // Loading state for auth operations
        userLoading,            // Loading state specifically for user fetches
        isAuthenticated: !!user,
        isLoggingOut,
        
        // Primary user function
        getUser,                // Async function to get user data
        
        // Permission functions
        hasPermission,          // Check if user has specific permission
        requirePermission,      // Throw error if permission not found
        
        // Actions
        login,
        logout,
        submit2FA,
        refreshUser,           // For backward compatibility
        requestPasswordReset,
        authenticateWithBiometric: authenticateWithBiometricFunc,
        
        // API utilities
        apiRequest,            // Enhanced API request helper
        
        // Utilities
        getCookie,
        clearAuthCookies,
        debugCookies,          // NEW: Debug function
        APP_URL,
        DEV,
        FILE_APP_URL,
        
        // For backward compatibility (if needed)
        handle2FASubmit: submit2FA
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};